import { useState, useEffect } from "react";
import { useAuth } from "@/lib/auth";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import PhotoUpload from "@/components/PhotoUpload";
import { toast } from "sonner";

const BIO_MAX = 500;

export default function EditProfileDialog({ open, onOpenChange }) {
  const { user, refresh } = useAuth();
  const [name, setName] = useState("");
  const [bio, setBio] = useState("");
  const [photoPath, setPhotoPath] = useState(null);
  const [showEmail, setShowEmail] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !user) return;
    setName(user.name || "");
    setBio(user.bio || "");
    setPhotoPath(user.profile_photo_path || null);
    setShowEmail(!!user.show_email);
  }, [open, user]);

  const initial = (user?.name || "L").charAt(0).toUpperCase();
  const previewSrc = photoPath ? fileUrl(photoPath) : null;

  const handlePhotoUploaded = async (path) => {
    // Persist photo immediately so the avatar updates right away.
    setPhotoPath(path);
    try {
      await api.patch("/auth/me", { profile_photo_path: path });
      await refresh();
      toast.success("Profile photo updated");
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't save photo");
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Name can't be empty");
      return;
    }
    setSaving(true);
    try {
      await api.patch("/auth/me", { name: name.trim(), bio: bio.trim(), show_email: showEmail });
      await refresh();
      toast.success("Profile saved");
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't save changes");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-lg" data-testid="edit-profile-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-forest">Your profile</DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="space-y-5 pt-2">
          <div className="flex items-center gap-4">
            <Avatar className="w-20 h-20 border-2 border-gold/60">
              {previewSrc && <AvatarImage src={previewSrc} alt={name} />}
              <AvatarFallback className="bg-forest text-ivory text-2xl">{initial}</AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <p className="text-xs uppercase tracking-widest text-sageGray mb-1.5">Profile photo</p>
              <PhotoUpload
                onUploaded={handlePhotoUploaded}
                label={photoPath ? "Change photo" : "Upload photo"}
                testId="profile-photo-upload"
              />
              <p className="text-[11px] text-sageGray mt-1.5">JPG, PNG, GIF, or WEBP, up to 10 MB.</p>
            </div>
          </div>

          <div>
            <Label className="text-forest">Display name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              maxLength={80}
              className="mt-1.5 bg-white border-sage focus-visible:ring-gold"
              data-testid="edit-profile-name-input"
            />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label className="text-forest">Bio</Label>
              <span className="text-[11px] text-sageGray" data-testid="bio-counter">
                {bio.length}/{BIO_MAX}
              </span>
            </div>
            <Textarea
              rows={4}
              value={bio}
              maxLength={BIO_MAX}
              onChange={(e) => setBio(e.target.value)}
              placeholder="A short note about you — where you grew up, what side of the family you're on, anything you'd like relatives to know."
              className="mt-1.5 bg-white border-sage focus-visible:ring-gold"
              data-testid="edit-profile-bio-input"
            />
          </div>

          <div className="flex items-start justify-between gap-4 p-4 rounded-lg bg-cream/60 border border-sage">
            <div className="flex-1 min-w-0">
              <Label className="text-forest cursor-pointer" htmlFor="show-email-toggle">Show my email to circle members</Label>
              <p className="text-xs text-sageGray mt-0.5">Off by default. Admins can always see your email regardless of this setting.</p>
            </div>
            <Switch
              id="show-email-toggle"
              checked={showEmail}
              onCheckedChange={setShowEmail}
              data-testid="show-email-toggle"
              className="data-[state=checked]:bg-forest"
            />
          </div>

          <div className="flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="text-sageGray hover:bg-cream rounded-full"
              data-testid="edit-profile-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-forest hover:bg-forestSoft text-ivory rounded-full px-6"
              data-testid="edit-profile-save"
            >
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
